import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Table,
  Stack,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Chip,
  Button,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useGetUserSelectionsQuery } from 'src/api/contests/getUserSelections';
import { useAppSearchParams } from 'src/hooks/useAppSearchParams';
import { SelectionType } from 'src/types/domain.types';
import { dateUtils } from 'src/utils/date';
import { useTimezoneStore } from 'src/store/timezoneStore';

type TableHeaderType = {
  id: string;
  label: string;
  align?: 'left' | 'right';
};

export default function ContestUserSelectionsPage() {
  const navigate = useNavigate();
  const { contestId, userId } = useAppSearchParams();
  const { timezone } = useTimezoneStore();

  const { data: selections = [], isLoading } = useGetUserSelectionsQuery({
    contestId,
    userId,
  });

  const tableHeader: TableHeaderType[] = [
    { id: 'contest_week_name', label: 'Round' },
    { id: 'match', label: 'Match' },
    { id: 'selection', label: 'Selection' },
    { id: 'is_correct', label: 'Is Correct' },
    { id: 'back_in', label: 'Back In' },
    { id: 'created_at', label: `Created At (${timezone})` },
  ];

  return (
    <>
      <Helmet>
        <title> User </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            User Selections
          </Typography>

          <Button
            variant='outlined'
            startIcon={<Iconify icon='eva:arrow-back-fill' />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Stack>

        <Card>
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {tableHeader.map(({ id, label }) => (
                        <TableCell key={id}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selections.map(
                      (selection: SelectionType, index: number) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component='th' scope='row'>
                            {selection.contest_week_name}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            {selection.match.teams?.[0].name_en} vs{' '}
                            {selection.match.teams?.[1].name_en}{' '}
                            {`(${selection.match.teams?.[0].score || 0} : ${
                              selection.match.teams?.[1].score || 0
                            })`}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            <Chip
                              label={
                                selection.team_id ===
                                selection.match.teams?.[0].team_id
                                  ? selection.match.teams?.[0].name_en
                                  : selection.match.teams?.[1].name_en
                              }
                              color='primary'
                            />
                          </TableCell>

                          <TableCell component='th' scope='row'>
                            {selection.is_correct === null ? (
                              <Iconify
                                icon={'eva:radio-button-off-outline'}
                                sx={{ color: 'grey' }}
                              />
                            ) : selection.is_correct ? (
                              <Iconify
                                icon={'eva:checkmark-circle-2-outline'}
                                sx={{ color: 'green' }}
                              />
                            ) : (
                              <Iconify
                                icon={'eva:close-circle-outline'}
                                sx={{ color: 'red' }}
                              />
                            )}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            {selection.is_back_in ? (
                              <Iconify
                                icon={'eva:checkmark-circle-2-outline'}
                                sx={{ color: 'green' }}
                              />
                            ) : (
                              <Iconify
                                icon={'eva:close-circle-outline'}
                                sx={{ color: 'red' }}
                              />
                            )}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            {dateUtils.formatForDisplay(
                              selection.created_at,
                              timezone
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
    </>
  );
}
